/* eslint-disable jsx-a11y/media-has-caption */

import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import {
    Button,
    Heading,
    Text,
    Glyphicon,
} from 'reaxl';
import { breakpoints } from 'axl-config';

import Head from 'next/head';
import { callToActionAnalytics } from '@/analytics';
import ParagraphWithAnalytics from '@/component/ParagraphWithAnalytics';
import { linksPreload } from '@/utilities';
import LazyComponent from '@/component/LazyComponent';

const preloadImg = (bgImageDesktop) => (
    <Head>
        {bgImageDesktop && linksPreload.preImage(bgImageDesktop)}
    </Head>
);

const contentClassName = clsx({
    'col-md-6': true,
    'display-flex': true,
    'flex-column': true,
    'justify-content-between': true,
    'padding-vertical-0': true,
});

const visualAssetClassName = clsx({
    visualAssetContainer: true,
    'col-md-5': true,
    'padding-right-md-4': true,
    'padding-bottom-0': true,
});
function BackgroundImageContainer({
    bgImgMobile,
    bgImgDesktop,
    bgPositionDesktop,
    bgPositionMobile,
    bgScalingDesktop,
    bgScalingMobile,
    children,
}) {
    return (
        <>
            <div
                data-cmp="marketingValueContainer"
                className="marketingValueContainerBackground"
                style={{
                    backgroundRepeat: 'no-repeat',
                    maxWidth: '1280px',
                    margin: '0 auto',
                }}
            >
                {children}
            </div>
            <style type="text/css">
                {`
              @media screen and (min-width: ${breakpoints.min.md}px) {
                .marketingValueContainerBackground {
                  background-image : ${bgImgDesktop ? `url(${bgImgDesktop})` : 'none'};
                  background-position: ${bgPositionDesktop || 'center'};
                  background-size: ${bgScalingDesktop || 'cover'};
                }
              }
              @media (max-width: ${breakpoints.max.sm}px) {
                .marketingValueContainerBackground {
                  background-image : ${bgImgMobile ? `url(${bgImgMobile})` : 'none'};
                  background-position: ${bgPositionMobile || 'center'};
                  background-size: ${bgScalingMobile || 'cover'};
                }
              }
          `}
            </style>
        </>
    );
}

function VideoContainer({ video, image }) {
    const [showVideo, setShowVideo] = useState(false);
    const videoElement = useRef(null);

    const triggerVideo = () => {
        setShowVideo(true);
        videoElement.current.play();
    };

    return (
        <div style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative' }}>
            <video
                preload="none"
                className={`${showVideo ? 'display-block' : 'display-none'}`}
                ref={videoElement}
                controls
                style={{ width: '100%', height: '100%', position: 'absolute' }}
            >
                <source src={video} />
            </video>
            <VisualAssetContainer
                className={`${showVideo ? 'display-none' : 'display-block'}`}
                image={image}
                onClick={triggerVideo}
            />
            <Glyphicon
                className="text-inverse"
                glyph="play-circle"
                onClick={triggerVideo}
                style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '50px', display: showVideo ? 'none' : 'block' }}
            />
        </div>
    );
}

function VisualAssetContainer({ image, imageScaling = 'cover', imagePosition = 'center', ...rest }) {
    return (
        <img
            alt="marketing value"
            data-cmp="marketingValueContainer_image"
            src={image}
            style={{ objectFit: imageScaling, height: '100%', width: '100%', objectPosition: imagePosition }}
            {...rest}
        />
    );
}

const handleDataLinkClick = (e) => callToActionAnalytics.fireMarketValueLINKClick(e, { co_txt_url: 'Search available vehicles' });

const renderBodyContent = (bodyText) => bodyText.map((group) => {
    const {
        subheader,
        paragraph,
    } = group;

    return (
        (subheader || paragraph) && (
            <div
                data-cmp="marketingValueContainer_content"
                className="padding-vertical-2"
            >
                {subheader && (
                    <div>
                        <Text
                            weight="bold"
                            size={400}
                            data-qaid="marketingValueContainer_content_header"
                        >
                            {subheader}
                        </Text>
                    </div>
                )}
                {paragraph && (
                    <ParagraphWithAnalytics
                        dataQaid="marketingValueContainer_content_copy"
                        paragraph={paragraph}
                        handleDataLinkClick={handleDataLinkClick}
                    />
                )}
            </div>
        )
    );
});

function MarketingValueContainer({ marketingValueData }) {
    const {
        headline,
        subheader,
        body_text: bodyText,
        text_color: textColor,
        external_link: isExternalLink,
        cta_text: ctaText,
        cta_button_type: ctaButtonType,
        destination_url: destinationUrl,
        background_color: bgColor,
        background_image_mobile: bgImageMobile,
        background_image_desktop: bgImageDesktop,
        background_position_desktop: bgPositionDesktop,
        background_position_mobile: bgPositionMobile,
        background_scaling_desktop: bgScalingDesktop,
        background_scaling_mobile: bgScalingMobile,
        image,
        image_scaling: imageScaling,
        image_position: imagePosition,
        video,
        ...rest
    } = marketingValueData;

    const handleCTAClick = (e) => {
        callToActionAnalytics.fireMarketValueCTAClick(e, ctaText);
    };

    const renderSideContent = () => (
        <div
            data-cmp="marketingValueContainer_side_content"
            className={contentClassName}
            style={{ color: textColor || '#2d363e' }}
        >
            {headline && (
                <Heading
                    className="padding-top-2"
                    data-qaid="marketingValueContainer_main_header"
                    size={700}
                >
                    {headline}
                </Heading>
            )}

            {subheader && (
                <Heading
                    data-qaid="marketingValueContainer_sub_header"
                    className="padding-bottom-2"
                    size={600}
                >
                    {subheader}
                </Heading>
            )}

            {bodyText && bodyText.length > 0 && renderBodyContent(bodyText)}

            {destinationUrl && ctaText && (
                <div className="padding-top-2">
                    <Button
                        data-cmp="marketingValueContainer_cta"
                        bsStyle={ctaButtonType}
                        href={destinationUrl}
                        onClick={handleCTAClick}
                    >
                        {ctaText}
                        {isExternalLink && <span className="margin-left-1 glyphicon glyphicon-offsite" />}
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <>
            {preloadImg(bgImageDesktop)}
            <div style={{ backgroundColor: bgColor || '#f8f8f8' }}>
                <BackgroundImageContainer
                    bgImgMobile={bgImageMobile}
                    bgImgDesktop={bgImageDesktop}
                    bgPositionDesktop={bgPositionDesktop}
                    bgPositionMobile={bgPositionMobile}
                    bgScalingDesktop={bgScalingDesktop}
                    bgScalingMobile={bgScalingMobile}
                    {...rest}
                >
                    <div className="container">

                        <div
                            className="marketingValueContentContainer col-md-10 col-md-offset-2"
                        >
                            <div className={visualAssetClassName}>
                                <LazyComponent
                                    threshold="50"
                                    componentClass="span"
                                >
                                    {video ? (
                                        <VideoContainer
                                            video={video}
                                            image={image}
                                            imageScaling={imageScaling}
                                            imagePosition={imagePosition}
                                        />
                                    )
                                        : (image
                                            && (
                                                <VisualAssetContainer
                                                    image={image}
                                                    imageScaling={imageScaling}
                                                    imagePosition={imagePosition}
                                                />
                                            ))}
                                </LazyComponent>
                            </div>
                            {renderSideContent()}
                        </div>
                    </div>
                </BackgroundImageContainer>
            </div>
        </>
    );
}

MarketingValueContainer.propTypes = {
    marketingValueData: PropTypes.object,
};

export default MarketingValueContainer;
